import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'umi';

import { getAiPrompts } from '@dbl/services/ai/api';
import type { IAiPromptItem } from '@dbl/services/ai/api';
import { LANGUAGE_EN } from '@home/_config/config';

import { CUSTOM_APPROACH_VALUE } from '../AiSettings/editorDraft';
import { normalizeTemplateCategory } from '../AiSettings/templateConstants';
import type { IAiPromptOption } from '../types';

const promptOptionsRefreshListeners = new Set<() => void>();

/** 保存模板等场景下主动触发分析思路列表重新请求（无跨会话缓存） */
export const invalidateAiPromptOptionsCache = (_gameCode?: string): void => {
  promptOptionsRefreshListeners.forEach((listener) => listener());
};

const subscribeAiPromptOptionsRefresh = (listener: () => void): (() => void) => {
  promptOptionsRefreshListeners.add(listener);
  return () => {
    promptOptionsRefreshListeners.delete(listener);
  };
};

/**
 * 兼容后端不同返回形态，把模板列表统一抽成 IAiPromptItem[]。
 * 这里只做“结构归一化”，不在这里做文案语言选择和 UI option 映射。
 */
const normalizePromptItems = (response: unknown): IAiPromptItem[] => {
  const payload = response as {
    list?: IAiPromptItem[];
    data?: { list?: IAiPromptItem[]; };
  };
  const list = Array.isArray(payload?.list) ? payload.list : payload?.data?.list;
  if (!Array.isArray(list)) {
    return [];
  }
  return list.filter((item) => Boolean(item?.uuid));
};

const fetchPromptItems = async (
  gameCode: string,
  dashboardId: string,
): Promise<IAiPromptItem[]> => {
  if (!gameCode) {
    return [];
  }
  const response = await getAiPrompts({
    game_code: gameCode,
    // 新建报表尚无 dashboardId 时仍请求列表，以便展示公共/业务预置思路
    ...(dashboardId ? { dashboard_id: dashboardId } : {}),
  });
  return normalizePromptItems(response);
};

export interface IUseAiPromptOptionsParams {
  gameCode: string;
  dashboardId: string;
  enabled: boolean;
}

/**
 * 从后端拉取 AI prompt 模板，并构造给下拉框的 `IAiPromptOption[]`。
 * - 无模块级缓存：enabled 且具备 gameCode 时请求 prompts/list（dashboardId 可选，新建报表无 id 亦拉取）；
 * - 失败降级：仅保留 `__custom__` 选项，确保弹窗仍可用。
 */
export const useAiPromptOptions = (params: IUseAiPromptOptionsParams): IAiPromptOption[] => {
  const { gameCode, dashboardId, enabled } = params;
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  // 自定义方案始终保底存在：
  // - 即使模板接口失败，用户也仍然可以手动输入 prompt 继续使用弹窗；
  // - 放在 useMemo 中是为了让 formatMessage 变化时同步刷新文案。
  const customPromptOption = useMemo<IAiPromptOption>(() => ({
    value: CUSTOM_APPROACH_VALUE,
    text: formatMessage({ id: 'dbl_ai_analysis_settings_custom_approach' }),
    info: {
      analysisContext: '',
      promptContent: '',
    },
  }), [formatMessage]);

  const [promptItems, setPromptItems] = useState<IAiPromptItem[]>([]);
  const [refreshTick, setRefreshTick] = useState<number>(0);

  useEffect(() => subscribeAiPromptOptionsRefresh(() => {
    setRefreshTick((prev) => prev + 1);
  }), []);

  useEffect(() => {
    let cancelled = false;
    // enabled=false 或缺少 gameCode：不请求；dashboardId 为空（新建报表）仍请求公共/业务模板
    if (!enabled || !gameCode) {
      setPromptItems([]);
    } else {
      fetchPromptItems(gameCode, dashboardId)
        .then((items) => {
          if (cancelled) {
            return;
          }
          setPromptItems(items);
        })
        .catch(() => {
          if (cancelled) {
            return;
          }
          setPromptItems([]);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [dashboardId, enabled, gameCode, refreshTick]);

  const promptOptions = useMemo<IAiPromptOption[]>(() => {
    const presetOptions: IAiPromptOption[] = promptItems.map((item) => {
      const {
        id,
        name_cn: nameCn,
        name_en: nameEn,
        name,
        template_category: templateCategory,
        analysis_context: analysisContext = '',
        prompt_content: promptContent = '',
        user_id: userId,
        user_name: userName,
      } = item ?? {};
      const text = isEn
        ? (nameEn || name || nameCn || id)
        : (nameCn || name || nameEn || id);
      const templateName = String(nameCn || name || nameEn || '');
      return {
        value: String(id ?? ''),
        text: String(text ?? ''),
        info: {
          analysisContext: String(analysisContext),
          promptContent: String(promptContent),
          templateCategory: normalizeTemplateCategory(templateCategory),
          templateName,
          userId: userId ? String(userId) : undefined,
          userName: userName ? String(userName) : undefined,
        },
      };
    });

    return [...presetOptions, customPromptOption];
  }, [customPromptOption, isEn, promptItems]);

  return promptOptions;
};
